import { Link } from 'react-router-dom';

export default function Roadmap() {
  const phases = [
    {
      phase: 'Phase 1',
      title: 'Foundation',
      status: 'Completed',
      items: [
        'Blockchain & Token Creation',
        'Smart Contract Development',
        'Block Explorer Launch',
        'PEP Token Distribution',
      ],
      color: 'from-green-500 to-emerald-600',
      icon: 'ri-checkbox-circle-fill',
    },
    {
      phase: 'Phase 2',
      title: 'Core Systems',
      status: 'In Progress',
      items: [
        'Multi-Asset Wallet Release',
        'Cloud Mining Hub Launch',
        'Staking Platform Live',
        'Mobile App Beta',
      ],
      color: 'from-cyan-500 to-blue-600',
      icon: 'ri-loader-fill',
    },
    {
      phase: 'Phase 3',
      title: 'AI Engine',
      status: 'Q2 2025',
      items: [
        'PepBot Auto-Trading Launch',
        'AI Market Predictor',
        'Risk Management Tools',
        'Performance Analytics',
      ],
      color: 'from-purple-500 to-pink-600',
      icon: 'ri-time-fill',
    },
    {
      phase: 'Phase 4',
      title: 'Experience Layer',
      status: 'Q3 2025',
      items: [
        '3D Dashboard Release',
        'AI Avatar Integration',
        'VR/AR Support',
        'Voice Command System',
      ],
      color: 'from-orange-500 to-red-600',
      icon: 'ri-time-fill',
    },
    {
      phase: 'Phase 5',
      title: 'Expansion',
      status: 'Q4 2025',
      items: [
        'PepVerse Metaverse Launch',
        'NFT Marketplace',
        'Cross-Chain Integration',
        'Global Exchange Listings',
      ],
      color: 'from-indigo-500 to-purple-600',
      icon: 'ri-time-fill',
    },
  ];

  return (
    <section className="py-20 sm:py-32 px-4 sm:px-6 relative overflow-hidden bg-gradient-to-b from-black via-gray-950 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Development Roadmap & Technical Milestones
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Planned infrastructure upgrades and feature implementations. Timeline subject to technical feasibility, regulatory approval, and market conditions.
          </p>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-500 via-purple-500 to-emerald-500"></div>

          <div className="space-y-12">
            {[
              {
                phase: 'Phase 1: Foundation (Completed)',
                status: 'Operational',
                items: [
                  'Smart contract deployment and security audit',
                  'KYC/AML compliance infrastructure',
                  'Basic trading and wallet functionality',
                  'Initial liquidity pool establishment'
                ],
                gradient: 'from-blue-500 to-cyan-500',
                icon: 'ri-checkbox-circle-fill'
              },
              {
                phase: 'Phase 2: Core Features (Current)',
                status: 'In Development',
                items: [
                  'Advanced trading engine with order types',
                  'Staking protocol with variable lock periods',
                  'Enhanced security monitoring systems',
                  'Regulatory reporting automation'
                ],
                gradient: 'from-purple-500 to-pink-500',
                icon: 'ri-loader-4-line'
              },
              {
                phase: 'Phase 3: Expansion (Planned Q3-Q4)',
                status: 'Subject to Approval',
                items: [
                  'Cross-chain bridge integration (pending audit)',
                  'Institutional custody solutions',
                  'Advanced risk analytics dashboard',
                  'API access for verified partners'
                ],
                gradient: 'from-emerald-500 to-teal-500',
                icon: 'ri-time-line'
              },
              {
                phase: 'Phase 4: Ecosystem (Future)',
                status: 'Conditional',
                items: [
                  'Governance framework implementation',
                  'Additional asset class support',
                  'Enhanced compliance tools',
                  'Third-party integration framework'
                ],
                gradient: 'from-orange-500 to-red-500',
                icon: 'ri-map-pin-line'
              }
            ].map((phase, index) => (
              <div key={index} className={`relative flex flex-col lg:flex-row gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                {/* Content Card */}
                <div className="flex-1 lg:w-1/2">
                  <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${phase.gradient} rounded-xl flex items-center justify-center`}>
                        <i className={`${phase.icon} text-white text-2xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{phase.phase}</h3>
                        <span className="text-sm text-gray-500">{phase.status}</span>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {phase.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-600">
                          <i className="ri-arrow-right-s-fill text-gray-400 mt-1"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Timeline Dot */}
                <div className="hidden lg:flex items-center justify-center w-16 flex-shrink-0">
                  <div className={`w-6 h-6 bg-gradient-to-br ${phase.gradient} rounded-full border-4 border-white shadow-lg`}></div>
                </div>

                {/* Spacer */}
                <div className="flex-1 lg:w-1/2"></div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-gray-50 border border-gray-200 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-gray-500 text-xl mt-0.5"></i>
            <div>
              <p className="text-gray-700 font-semibold mb-1">Development Disclaimer</p>
              <p className="text-gray-600 text-sm">
                Roadmap represents current development intentions only. Features may be delayed, modified, or cancelled based on technical constraints, regulatory requirements, security considerations, or market conditions. No guarantee of implementation or timeline accuracy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
